import BlockIcon from '@mui/icons-material/Block';
import DoneIcon from '@mui/icons-material/Done';
import { DialogProps } from '@mui/material/Dialog/Dialog';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { ListContextProvider, useList } from 'react-admin';
import { Button } from 'shared/mui/Buttons';
import { CardActionsButtons } from 'shared/mui/Card';
import { CardContent } from 'shared/mui/Card/CardContent/CardContent';
import { CardHeader } from 'shared/mui/Card/CardHeader/CardHeader';
import { Dialog } from 'shared/mui/Dialogs';
import { CloseIconButton } from 'shared/mui/IconButtons';
import { DateTimeField, FunctionField, TextField } from 'shared/react-admin/Fields';
import { ReferenceOneField } from 'shared/react-admin/ReferenceFields';
import { GatewayRuleInfo, OrderMethod } from 'types/embedded/orderMethod';

import { DatagridStyled } from './OrderMethodsRulesShowDialog.styled';

type Props = {
  orderMethod: OrderMethod | undefined;
  onClose: () => void;
} & DialogProps;

const renderIcon = (ruleInfo: GatewayRuleInfo) =>
  ruleInfo.priority ? (
    <DoneIcon color="success" sx={{ fontSize: '18px' }} />
  ) : (
    <BlockIcon color="warning" sx={{ fontSize: '18px' }} />
  );

const OrderMethodsRulesShowDialog = ({ orderMethod, onClose, open }: Props): JSX.Element => {
  const translate = useTypedTranslate();

  const context = useList({
    data: orderMethod?.gatewayRuleInfo ?? [],
    perPage: 1000,
    sort: { field: 'originId', order: 'ASC' },
  });

  if (!orderMethod) {
    return <></>;
  }

  return (
    <Dialog fullWidth maxWidth="lg" onClose={onClose} open={open}>
      <ListContextProvider value={context}>
        <CardHeader
          appendActions={[<CloseIconButton key="close-button" onClose={onClose} />]}
          title={translate('app.orderMethods.rulesDialog.title')}
          titleSize="smallBold"
        />
        <CardContent>
          <DatagridStyled bulkActionButtons={false} rowClick={false}>
            <TextField label="ID" source="id" />
            <DateTimeField
              inline
              label={translate('app.orderMethods.rulesDialog.updatedAt')}
              source="updatedAt"
            />
            <ReferenceOneField
              label={translate('app.orderMethods.rulesDialog.ruleName')}
              link="show"
              reference="gateway_rules"
              target="id"
            >
              <TextField source="name" sx={{ textDecoration: 'underline' }} />
            </ReferenceOneField>
            <FunctionField
              label={translate('app.orderMethods.rulesDialog.useInTest')}
              render={renderIcon}
            />
          </DatagridStyled>
        </CardContent>
        <CardActionsButtons
          rightActionsSlot={
            <Button label={translate('app.buttons.close')} onClick={onClose} variant="outlined" />
          }
        />
      </ListContextProvider>
    </Dialog>
  );
};

export default OrderMethodsRulesShowDialog;
